import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { transformTaskFormValues } from 'utils/DataTransform/Task/taskForm';
import { MEDIA_SERVICE } from 'utils/constants/media';
import { translatedKeyValueText } from 'utils/methods/helpers';
import { mapIconSelector, taskValidationSchema } from 'utils/formFields/task';
import { serviceTypes } from 'utils/constants/serviceTypes';
import { TASK_TYPE } from 'utils/constants/task';
import { detailedSection, generalSection, getInitialValues } from '../options';
import { getTimezone } from 'reducers/utils';
import {
  getRegionId,
  getSecurityCompanyName,
  getSecurityId,
  getUserAssignedDepartmentList,
} from 'reducers/users';
import { getRegion } from 'reducers/region';
import {
  getSecurityCompanyServices,
  getSitesEnabled,
} from 'reducers/securityCompany';
import TaskForm from '../index';
import { sitesMapConfig } from 'utils/constants/sites';
import { getTaskLocationMarker } from 'utils/helper/task';
import { get, isEmpty, merge } from 'lodash';
import { GEOGRAPHICAL_MAP } from 'utils/constants/Map';
import API from 'services/api';
import { transformRaw } from 'utils/DataTransform/Personnel/PersonnelList';
import { ROLES } from 'utils/constants/roles';
import { SUBTASK_CLEANING_DEFAULT } from 'utils/constants/taskCleaner';
import { t } from 'i18next';
import { getCurrentPage, toggleModal } from 'reducers/ui';
import { PAGE_NAME } from 'utils/constants';
import { siteSelectors } from 'containers/Pages/Locations/Sites';
import { actions as embeddedFormActions } from 'modules/embeddedForms/reducer';
import { departmentExistInSites } from 'containers/Pages/Tasks/PatrolTemplates/Layouts/PatrolTemplateForm';

const RootTask = ({
  id,
  editMode,
  closeForm,
  taskDetails,
  regionId,
  regionName,
  companyId,
  companyName,
  services,
  defaultValues,
  allowSubTask,
  createTask,
  editTask,
  allowedTaskType,
  formTitle,
  subtaskColumns,
  subtaskDefaultValues,
  headerContent,
  resetValues,
  timezone,
  schedulingView,
  hideAssignee,
  omitFields = { generalSection: [], detailedSection: [] },
  subtaskOmitFields = {
    generalSection: [],
    detailedSection: [],
  },
  multiSchedule,
  allowToArchive,
  userAssignedDepartments = [],
  siteEnabled,
  persistentStateName,
  view,
  disablePresist,
  embeddedSiteDetails,
  clearEmbeddedFormDetails,
  businessClients = [],
  sites = [],
  rawClients,
  users = {},
  size,
  setResetValues,
}) => {
  const [surfaceImage, setSurfaceImage] = useState(GEOGRAPHICAL_MAP);
  const [path, setPath] = useState();
  const [selectedSiteLocation, setSiteLocation] = useState([]);
  const [center, setCenter] = useState(null);
  const [showPolylines, setShowPolyline] = useState(false);
  const [addSchedule, setAddSchedule] = useState(false);
  const [activeSchedule, setActiveSchedule] = useState();
  const [siteId, setSiteId] = useState('');
  const [location, setLocation] = useState([]);
  const [selectedSiteDetailImages, setSelectedSiteDetailImages] = useState();
  const [selectedTaskType, setSelectedTaskType] = useState(
    defaultValues.taskType,
  );
  const [assigneeList, setAssigneeList] = useState();
  const [isSurfaceImageMap, setIsSurfaceImageMap] = useState(false);
  const [selectedDepartments, setSelectedDepartments] = useState([]);

  useEffect(() => {
    if (siteId && siteId !== '') {
      const selectedSite = sites.find(s => s.id === siteId);
      if (selectedSite) {
        setSelectedSiteDetailImages(selectedSite.surfaces);
        if (!isEmpty(embeddedSiteDetails)) clearEmbeddedFormDetails();
      } else {
        setSelectedSiteDetailImages([]);
      }
    }
    const allowedAssigneeOptions = async role => {
      //fetching all users according to the taskType/service
      const users = await API.fetchUserList({ roles: role });
      if (users) {
        const assigneeRaw = transformRaw(users.data.data);
        setAssigneeList(creatingAssigneeList({ assigneeRaw }));
      }
    };
    if (selectedTaskType === serviceTypes.maintenance) {
      allowedAssigneeOptions([ROLES.MAINTAINER.id]);
    } else if (selectedTaskType === serviceTypes.cleaning) {
      allowedAssigneeOptions([ROLES.CLEANER.id]);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId, selectedTaskType]);

  const creatingAssigneeList = ({ assigneeRaw }) => {
    const rawAssigneeOptions = assigneeRaw?.filter(item =>
      item.services.some(task => task === selectedTaskType),
    );
    //creating a structured format for the assignee options to be able to read
    return rawAssigneeOptions?.map(item => ({
      id: item.id,
      name: item.label,
      photoUrl: item.value.general.photoUrl,
    }));
  };

  // surfaceImages represents surfaces used in tasks and subtasks
  const surfaceImages = [];
  const convertImageCoordinates = ({ values }) => {
    let valuesWithImageCoordinates = values;
    // check if the siteSurfaceImage is a map
    if (values.siteSurfaceImages.id === GEOGRAPHICAL_MAP.id) {
      // don't  check for the location array as it will not need conversion
      // then check if it has subtask array?
      if (values?.subtasks?.length > 0) {
        // this has subtasks
        valuesWithImageCoordinates = { ...values }; // create a new refrence of the original object
        valuesWithImageCoordinates.subtasks.forEach(element => {
          convertImageCoordinates({ values: element });
        });
      }
    } else {
      // it is an image
      // change the location array
      const { imageDimensions, siteSurfaceImages } = values;
      // push unique sub task site surface images to surfaceImages array
      if (
        siteSurfaceImages.id !== GEOGRAPHICAL_MAP.id &&
        !surfaceImages.find(f => f.id === siteSurfaceImages.id)
      ) {
        surfaceImages.push(siteSurfaceImages);
      }

      if (!isEmpty(values.location)) {
        values.location = values.location.map(m => {
          return {
            ...m,
            lat: parseFloat((1 - m.lng / imageDimensions.height).toFixed(2)),
            lng: parseFloat((m.lat / imageDimensions.width).toFixed(2)),
          };
        });
      }

      valuesWithImageCoordinates = { ...values };
      if (values?.subtasks && values.subtasks.length > 0) {
        valuesWithImageCoordinates.subtasks.forEach(element => {
          convertImageCoordinates({ values: element });
        });
      }
    }

    return valuesWithImageCoordinates;
  };

  const addParentTaskTypeToSubTasks = ({ transformedData, parentTaskType }) => {
    let transformedValues = transformedData;
    // then check if it has subtask array?
    if (transformedData?.subtasks?.length > 0) {
      // this has subtasks
      transformedValues = { ...transformedData };
      transformedValues.subtasks.forEach(element => {
        if (element.taskType !== parentTaskType) {
          element['taskType'] = parentTaskType; // set parentTaskType to subTask
        }
        addParentTaskTypeToSubTasks({ values: element, parentTaskType });
      });
    }

    return transformedValues;
  };
  const getTransformedValues = ({ id, task, values }) => {
    //convert image points to percentages if any
    const transformedData = convertImageCoordinates({
      values,
      parentTaskType: values.taskType,
    });
    const transformedDataWithParentTaskType = addParentTaskTypeToSubTasks({
      transformedData,
      parentTaskType: values.taskType,
    });
    const data = transformTaskFormValues({
      id,
      task,
      values: {
        ...transformedDataWithParentTaskType,
        mediaService: MEDIA_SERVICE.TASK,
      },
      additionalMarkers: values.additionalMarkers,
      regionId,
      regionName,
      companyId,
      companyName,
      businessClients,
      rawClients,
      sites,
      timezone,
      multiSchedule,
      users,
    });
    // add the used surface images to surface array
    data.surfaces = [...data.surfaces, ...surfaceImages];

    return data;
  };

  const monitorFormChanges = vals => {
    setSelectedDepartments(vals?.departments);
    if (vals?.departments && vals?.departments.length > 0) {
      // setSelectedDepartments(vals?.departments);
      // check if the selected department exists in the selected sites
      // if yes, then don't change anything
      // if no, then remove the selected option from the site select field
      if (!isEmpty(vals.site) && !departmentExistInSites(vals)) {
        vals.site = '';
        // default sitesurfaceimages to geographical map
        vals.siteSurfaceImages = GEOGRAPHICAL_MAP;
        // clear the checkpoints
        vals.checkpoints = [];
      }
    }
    if (!isEmpty(vals.location) && !isEmpty(vals.taskType)) {
      vals.location[0].icon = TASK_TYPE[vals.taskType.category].mapIcon;
    }
    if (vals.site && vals.site?.id) {
      const ss = sites.find(s => s.id === vals.site?.id);
      //PFM-2172 the loaded site may not be found in the list of sites due to archiving so remove it
      if (ss && (!vals.center || vals.location)) {
        setSiteId(vals.site?.id);
        setSiteLocation(vals.site);
        if (ss.centreOfGravity) {
          const { latitude, longitude } = ss.centreOfGravity;
          if (!center) {
            setCenter({
              lat: latitude,
              lng: longitude,
            });
          } else if (latitude !== center.lat && longitude !== center.lng) {
            setCenter({
              lat: vals.site.centreOfGravity.latitude,
              lng: vals.site.centreOfGravity.longitude,
            });
          }
        }
      }
    } else {
      setSiteId('');
      setSelectedSiteDetailImages();
      setSiteLocation();
    }

    if (
      vals.siteSurfaceImages &&
      vals.siteSurfaceImages.id === GEOGRAPHICAL_MAP.id
    ) {
      setIsSurfaceImageMap(true);
    } else {
      setIsSurfaceImageMap(false);
    }

    if (vals.center && vals.center.setToCenter) {
      setCenter({
        lat: vals.center.lat,
        lng: vals.center.lng,
      });
      vals.center.setToCenter = false;
    }
    if (vals.newBusinessClient) {
      vals.newSiteContact = false;
    } else {
      vals.newContact = false;
    }
    if (vals.siteLocations && vals.siteLocations.length > 0) {
      const sitePaths = vals.siteLocations[0].paths;
      if (sitePaths && sitePaths.length > 2) {
        setPath(sitePaths);
      }
    }
    // additionalMarkers gets passed on by sub-task form
    if (vals.additionalMarkers && vals.additionalMarkers.length > 0) {
      vals.additionalMarkers = vals.additionalMarkers.map(item => ({
        ...getTaskLocationMarker({
          id: item.id,
          showPolylines: vals.subtasksSequentialOrder,
          lat: item.lat,
          lng: item.lng,
          text: item.text,
          seqNumber: item.seqNumber,
          surfaceUuid: item.surfaceUuid,
          icon: mapIconSelector(vals.taskType),
          subtaskId: item.subtaskId,
        }),
      }));
    }

    if (vals.schedule && vals.schedule.activeTab) {
      setActiveSchedule(vals.schedule.activeTab);
    }
    setShowPolyline(vals.subtasksSequentialOrder);

    setAddSchedule(vals.addSchedule);
    setSelectedTaskType(vals.taskType);
    if (vals?.siteSurfaceImages) {
      setSurfaceImage(vals.siteSurfaceImages);
    } else {
      setSurfaceImage(GEOGRAPHICAL_MAP);
    }
    if (vals.location) {
      setLocation(vals.location);
    }
    if (resetValues) {
      // The second object will overwrite or add to the base object(vals). undefined values are not copied.
      merge(vals, resetValues); // merge the reset values with the current formik state values
      typeof setResetValues === 'function' && setResetValues(); // clear the values
    }
  };

  const onCreate = async values => {
    const data = getTransformedValues({ values });
    createTask({ data, rootTaskId: data.tasks[0].id });
  };

  const onEdit = async values => {
    const data = getTransformedValues({
      id: values.id,
      values,
      task: taskDetails,
    });

    editTask({ id: values.id, data, rootTaskId: values.id });
  };

  const ispopupRequired = () => {
    // newsite is clicked and a location is markerd on the surface
    if (location.length > 0 && siteId !== '') {
      return true;
    } else return !isSurfaceImageMap && location.length === 0;
  };
  // parent task type
  const taskType = taskDetails?.tasks?.find(item => item.id === id);

  const initialValues = editMode
    ? getInitialValues({
        id,
        values: taskDetails,
        defaultValues,
        taskType: taskType.taskType,
        subTaskDefaultValues: defaultValues,
        allowSubTask,
        multiSchedule,
        businessClients,
        sites,
        users,
        timezone,
      })
    : defaultValues;

  const filteredDepartments =
    !isEmpty(selectedSiteLocation) &&
    !isEmpty(selectedSiteLocation.assignedDepartmentList)
      ? userAssignedDepartments.filter(f =>
          selectedSiteLocation.assignedDepartmentList.includes(f.id),
        )
      : userAssignedDepartments;

  const generalSectionFields = generalSection({
    popUpRequired: ispopupRequired(),
    disabledFields: defaultValues.disabledFields,
    location,
    siteEnabled,
    allowSubTask,
    allowedTaskType,
    hideAssignee,
    omitFields: omitFields.generalSection,
    multiSchedule,
    assigneeList,
    companyId,
    selectedDepartments,
    userAssignedDepartments: filteredDepartments,
    services,
    view,
    selectedTaskType,
    updatedValues: {
      ...(!isEmpty(embeddedSiteDetails) && { site: embeddedSiteDetails }),
    },
  });

  const selectSubtaskDefaultValues = parentTaskType => {
    if (parentTaskType === serviceTypes.cleaning) {
      return SUBTASK_CLEANING_DEFAULT;
    }
    return subtaskDefaultValues;
  };

  const constructSubTaskType = parentTaskType => {
    if (parentTaskType === serviceTypes.cleaning) {
      return [
        {
          id: TASK_TYPE.CLEANING.id,
          name: TASK_TYPE.CLEANING.name,
        },
      ];
    } else {
      return [
        {
          id: TASK_TYPE.MAINTENANCE.id,
          name: TASK_TYPE.MAINTENANCE.name,
        },
      ];
    }
  };

  useEffect(() => {
    return () => {
      // clear embedded details
      if (!isEmpty(embeddedSiteDetails)) clearEmbeddedFormDetails();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embeddedSiteDetails]);

  return (
    <TaskForm
      size={size}
      headerContent={headerContent}
      editMode={editMode}
      initialValues={initialValues}
      formTitle={
        editMode ? formTitle || 'Edit task' : formTitle || 'Create task'
      }
      saveHoverText={
        editMode
          ? translatedKeyValueText('update_value_title', {
              value: taskDetails.tasks[0].name,
            })
          : formTitle || t('Create task')
      }
      closeForm={closeForm}
      onEdit={onEdit}
      onCreate={onCreate}
      validationSchema={taskValidationSchema({
        addSchedule,
        activeSchedule,
      })}
      onChange={monitorFormChanges}
      allowToArchive={allowToArchive}
      generalSection={generalSectionFields}
      detailedSection={detailedSection({
        location,
        selectedSiteDetailImages,
        surfaceImage,
        defaultValues,
        allowSubTask,
        columns: subtaskColumns,
        taskDetails,
        showPolylines,
        siteLocation: !isEmpty(path)
          ? [
              {
                ...sitesMapConfig,
                paths: path.map(point => ({
                  lat: point.lat,
                  lng: point.lng,
                })),
              },
            ]
          : get(selectedSiteLocation, 'locationDetail', []),
        center: center || initialValues.center,
        subtaskDefaultValues: selectSubtaskDefaultValues(selectedTaskType),
        isRootTask: true,
        allowedTaskType: constructSubTaskType(selectedTaskType),
        scheduleActiveTab: initialValues.schedule
          ? initialValues.schedule.activeTab
          : undefined,
        schedulingView,
        hideAssignee,
        omitFields: omitFields.detailedSection,
        subtaskOmitFields,
        multiSchedule,
        editMode,
        users,
        selectedTaskType,
        siteEnabled,
      })}
      persistentStateName={persistentStateName}
      disablePresist={disablePresist}
    />
  );
};

RootTask.propTypes = {
  id: PropTypes.string,
  editMode: PropTypes.bool,
  clearEmbeddedFormDetails: PropTypes.func,
  closeForm: PropTypes.func.isRequired,
  taskDetails: PropTypes.object,
  embeddedSiteDetails: PropTypes.object,
  regionId: PropTypes.string,
  regionName: PropTypes.string,
  companyId: PropTypes.string,
  companyName: PropTypes.string,
  services: PropTypes.array,
  defaultValues: PropTypes.object,
  allowSubTask: PropTypes.bool,
  siteEnabled: PropTypes.bool,
  createTask: PropTypes.func,
  editTask: PropTypes.func,
  allowedTaskType: PropTypes.array,
  formTitle: PropTypes.string,
  subtaskColumns: PropTypes.array,
  allowCreateSubtask: PropTypes.bool,
  subtaskDefaultValues: PropTypes.object,
  headerContent: PropTypes.object,
  resetForm: PropTypes.bool,
  setResetForm: PropTypes.func,
  toggleTaskform: PropTypes.func,
  resetValues: PropTypes.object,
  timezone: PropTypes.string,
  persistentStateName: PropTypes.string,
  size: PropTypes.string,
  view: PropTypes.string,
  schedulingView: PropTypes.array,
  hideAssignee: PropTypes.bool,
  omitFields: PropTypes.object,
  multiSchedule: PropTypes.bool,
  subtaskOmitFields: PropTypes.object,
  setResetValues: PropTypes.func,
  selectedSiteDetailImages: PropTypes.array,
  userAssignedDepartments: PropTypes.array,
  allowToArchive: PropTypes.bool,
  disablePresist: PropTypes.bool,
  businessClients: PropTypes.array,
  sites: PropTypes.array,
  rawClients: PropTypes.array,
  users: PropTypes.object,
};

const mapStateToProps = state => ({
  regionId: getRegionId(state),
  regionName: getRegion(state).name,
  companyId: getSecurityId(state),
  companyName: getSecurityCompanyName(state),
  services: getSecurityCompanyServices(state),
  timezone: getTimezone(state),
  userAssignedDepartments: getUserAssignedDepartmentList(state),
  siteEnabled: getSitesEnabled(state),
  view: getCurrentPage(state),
  embeddedSiteDetails: siteSelectors.siteEmbeddedDetails(state),
});

const mapDispatchToProps = dispatch => ({
  toggleTaskform: () => dispatch(toggleModal(PAGE_NAME.CLEANING_TASKS)),
  clearEmbeddedFormDetails: () =>
    dispatch(embeddedFormActions.clearEmbeddedFormDetails()),
});
export default connect(mapStateToProps, mapDispatchToProps)(RootTask);
